import type { CacheProxy, Deal } from '@paintscout/api'
import {
  updateCachedDeal,
  useSaveDealMutation,
  useDeleteDealsMutation,
  useArchiveDealsMutation,
  useUntrashDealsMutation,
  DealDocument,
  SearchDealsDocument,
  SearchDealsByCompanyDocument,
  SearchDealsByStageDocument,
  SearchQuotesDocument,
  updateCachedDealPartially
} from '@paintscout/api'
import { useApolloClient } from '@apollo/react-hooks'
import type ApolloCache from '@wora/apollo-cache'
import { getOperationName } from 'apollo-link'
import { useMemo } from 'react'
import { removeStoredMutationsById } from '../graphql/util/removeStoredMutationsById'
import { scheduleDelayedRefetch } from '../graphql/util/scheduleDelayedRefetch'

/**
 * Apollo matches `refetchQueries` strings against operation names by exact equality, and
 * apollo-client 2.6.10 does NOT warn about a name that matches nothing — a typo is a silent no-op.
 * These are resolved from the generated documents so a rename can't leave a dead string behind
 * (the `deal` refetch spent an unknown amount of time spelled `'getDeal'` — #22098).
 *
 * KEEP THESE AS FUNCTIONS — do not "simplify" them into module-level consts. `getOperationName` would
 * then run at import time, and because `vi.mock` factories are hoisted, every spec that mocks
 * `@paintscout/api` (e.g. `BoardView.spec.tsx`) would have to stub each of these documents just to
 * import this module, failing with "No <X>Document export is defined on the mock".
 */
const saveDealRefetchQueries = () => [
  getOperationName(SearchDealsDocument),
  getOperationName(DealDocument),
  getOperationName(SearchDealsByCompanyDocument),
  getOperationName(SearchDealsByStageDocument)
]

/**
 * Restoring a deal relinks its estimates/invoices server-side, so the quote lists need refreshing too.
 * `deal` matters most here: `untrashDeals` returns only a `BulkDocsResponseObject`, so the cache write
 * can't supply the restored deal the way `saveDeal`'s `...FullDeal` response can.
 */
const untrashDealsRefetchQueries = () => [
  getOperationName(SearchDealsDocument),
  getOperationName(DealDocument),
  getOperationName(SearchDealsByCompanyDocument),
  getOperationName(SearchQuotesDocument)
]

/**
 * Returns common useMutations for saving deals and handles updating the caches
 */
export function useDealMutations() {
  const [saveDealMutation] = useSaveDealMutation()
  const [deleteDealsMutation] = useDeleteDealsMutation()
  const [archiveDealsMutation] = useArchiveDealsMutation()
  const [untrashDealsMutation] = useUntrashDealsMutation()

  const client = useApolloClient()

  return useMemo(
    () => ({
      async saveDeal({
        deal: incomingDeal,
        delayedRefetchQueries,
        updateLinkedQuoteAddresses
      }: {
        deal: Deal
        delayedRefetchQueries?: string[]
        /**
         * Copy the deal's new location onto linked estimates/invoices whose address differs
         * (ENG-964). Set from the "Update Deal Location?" dialog.
         */
        updateLinkedQuoteAddresses?: boolean
      }) {
        // A save must never delete. `saveDeal` takes an untyped `JSON` arg and the server PUTs the deal
        // more or less verbatim, so a `_deleted: true` riding along tombstones the document in CouchDB and
        // drops it from Elasticsearch. Older clients cached exactly that on a soft delete, and those
        // values are still sitting in users' offline IndexedDB caches, so drop it on the way out rather
        // than trusting the cache. `putDeal` strips it server-side too. (#22138)
        const { _deleted: _neverSendDeleted, ...rest } = incomingDeal
        const deal = rest as Deal

        const timeOfSave = Date.now()
        const mutationId = `save-deal-${deal._id}`
        const result = await saveDealMutation({
          variables: {
            deal,
            updateLinkedQuoteAddresses
          },
          context: {
            __mutation_id__: mutationId
          },

          optimisticResponse: {
            saveDeal: {
              __typename: 'Deal' as const,
              ...deal,
              owner: deal.owner
                ? {
                    __typename: 'Owner',
                    ...deal.owner
                  }
                : null,
              stage: deal.stage
                ? {
                    __typename: 'DealStage',
                    ...deal.stage
                  }
                : null,
              pipeline: deal.pipeline
                ? {
                    __typename: 'DealPipeline',
                    ...deal.pipeline
                  }
                : null,
              contact: deal.contact
                ? {
                    __typename: 'DealContact',
                    ...deal.contact
                  }
                : null,
              jobCosting: deal.jobCosting
                ? {
                    __typename: 'JobCosting',
                    ...deal.jobCosting
                  }
                : null,
              // null is important, if its undefined it will break cache for deal (undefined is not valid
              // graphql value) — a new deal has no _rev yet, so coerce rather than trusting the caller.
              // Without this, the optimistic write leaves `Deal:<id>` missing `_rev`, which makes every
              // read of the (now correctly keyed) `deal({"id":...})` entry incomplete. (#22098)
              _rev: deal._rev ?? null
            }
          },
          update(cache: CacheProxy, { data }) {
            if (data?.saveDeal) {
              updateCachedDeal(cache, data.saveDeal)
            }
          },
          refetchQueries: saveDealRefetchQueries()
        })

        removeStoredMutationsById(client, mutationId, { before: timeOfSave })

        if (delayedRefetchQueries?.length) {
          scheduleDelayedRefetch(client, delayedRefetchQueries)
        }

        return {
          deal: result.data?.saveDeal
        }
      },

      async deleteDeals(ids: string[], skipRefetchQueries = false) {
        return deleteDealsMutation({
          variables: { ids },
          optimisticResponse: {
            deleteDeals: ids.map((id) => ({
              __typename: 'BulkDocsResponseObject',
              _id: id,
              _rev: null,
              error: null
            }))
          },
          refetchQueries: skipRefetchQueries ? [] : [getOperationName(SearchDealsDocument)],
          update(cache: ApolloCache, { data: _data }) {
            ids.forEach((id) => {
              // Mark deal as trashed so it's filtered out from searches.
              //
              // Deliberately NOT `_deleted: true`. This is a soft delete — the server keeps the document
              // and `EditDeal` still renders it behind a RecoveryNotice — but `_deleted` is CouchDB's
              // tombstone marker, so caching it here meant any later `saveDeal` that round-tripped this
              // deal would hard-delete it for real. `removeFromLists` says what we actually want. (#22138)
              updateCachedDealPartially(cache, id, { trashed: true }, { removeFromLists: true })
            })
          }
        })
      },
      async untrashDeals(ids: string[]) {
        return untrashDealsMutation({
          variables: { ids },
          refetchQueries: untrashDealsRefetchQueries(),
          update(cache: ApolloCache, { data: _data }) {
            ids.forEach((id) => {
              updateCachedDealPartially(cache, id, { trashed: false })
            })
          }
        })
      },

      async archiveDeals(
        dealIds: string[],
        { unarchive, skipRefetchQueries = false }: { unarchive?: boolean; skipRefetchQueries?: boolean } = {}
      ) {
        return archiveDealsMutation({
          variables: { ids: dealIds, unarchive },
          refetchQueries: skipRefetchQueries ? [] : [getOperationName(SearchDealsDocument)],
          optimisticResponse: {
            archiveDeals: dealIds.map((id) => ({
              __typename: 'BulkDocsResponseObject',
              _id: id,
              _rev: null,
              error: null,
              reason: null
            }))
          },
          update(cache: ApolloCache, { data: _data }) {
            dealIds.forEach((id) => {
              // we can fake the deal document as just an object with archived flag
              updateCachedDealPartially(cache, id, { archived: !unarchive })
            })
          }
        })
      }
    }),
    [client]
  )
}
